/**
 * src/lib/api.ts
 *
 * Central HTTP client for all WordPress / WooCommerce REST API calls.
 * Reads VITE_WP_API_URL from environment and attaches JWT auth headers.
 */

const BASE = (import.meta.env.VITE_WP_API_URL as string) ?? 'https://cleario.uk/wp-json/cleario/v1';

// ── Token storage ──────────────────────────────────────────────────────────────
export const getToken  = (): string | null => localStorage.getItem('cleario_token');
export const setToken  = (t: string): void  => localStorage.setItem('cleario_token', t);
export const clearToken = (): void          => localStorage.removeItem('cleario_token');

// ── Core fetch wrapper ─────────────────────────────────────────────────────────
async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  auth = false
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (auth) {
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  const res = await fetch(`${BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error((err as { message?: string }).message ?? `API error ${res.status}`);
  }
  return res.json() as Promise<T>;
}

// ── Auth ───────────────────────────────────────────────────────────────────────
export const authApi = {
  login: (username: string, password: string) =>
    apiFetch<{ token: string; user: WPUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  register: (data: { email: string; password: string; first_name: string; last_name: string }) =>
    apiFetch<{ token: string; user: WPUser }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  me: () => apiFetch<{ user: WPUser }>('/auth/me', {}, true),
};

// ── CMS ────────────────────────────────────────────────────────────────────────
export const cmsApi = {
  getAll:      () => apiFetch<CMSData>('/cms'),
  getPage:     (page: string) => apiFetch<CMSPage>(`/cms/${page}`),
  getSection:  (page: string, section: string) => apiFetch<unknown>(`/cms/${page}/${section}`),
};

// ── Products ───────────────────────────────────────────────────────────────────
export const productsApi = {
  getAll:       () => apiFetch<WCProduct[]>('/products'),
  getServices:  () => apiFetch<WCProduct[]>('/products/services'),
  getItems:     () => apiFetch<WCProduct[]>('/products/items'),
  getBySlug:    (slug: string) => apiFetch<WCProduct>(`/products/by-slug/${slug}`),
  getLoadSizes: () => apiFetch<WCLoadSize[]>('/products/load-sizes'),
};

// ── Forms ──────────────────────────────────────────────────────────────────────
export const formsApi = {
  contact: (data: {
    first_name: string; last_name: string; email: string;
    phone?: string; message: string;
  }) => apiFetch<{ success: boolean }>('/forms/contact', {
    method: 'POST', body: JSON.stringify(data),
  }),

  quote: (data: {
    first_name: string; last_name?: string; email: string; phone?: string;
    postcode?: string; service?: string; message?: string;
  }) => apiFetch<{ success: boolean }>('/forms/quote', {
    method: 'POST', body: JSON.stringify(data),
  }),

  newsletter: (email: string) =>
    apiFetch<{ success: boolean }>('/forms/newsletter', {
      method: 'POST', body: JSON.stringify({ email }),
    }),
};

// ── Stripe ─────────────────────────────────────────────────────────────────────
export const stripeApi = {
  /** GET /stripe/config — returns the Stripe publishable key from WP settings */
  getConfig: () =>
    apiFetch<{ publishable_key: string }>('/stripe/config'),

  /** POST /stripe/create-payment-intent — creates a PaymentIntent for the given order */
  createPaymentIntent: (data: {
    amount:   number;
    order_id: number;
    email:    string;
    name:     string;
  }) =>
    apiFetch<{ client_secret: string }>('/stripe/create-payment-intent', {
      method: 'POST',
      body:   JSON.stringify(data),
    }),
};

// ── Orders ─────────────────────────────────────────────────────────────────────
export const ordersApi = {
  /**
   * Cash booking — creates a WC order with status "on-hold" (pay on collection).
   * Backend sends confirmation email and returns order details.
   */
  create: (payload: BookingPayload) =>
    apiFetch<{ order_id: number; order_key: string; total: string; status: string }>(
      '/orders',
      { method: 'POST', body: JSON.stringify({ ...payload, payment_method: 'cash' }) },
      true
    ),
  list: () => apiFetch<WCOrder[]>('/orders', {}, true),

  get: (id: number) => apiFetch<WCOrder>(`/orders/${id}`, {}, true),
};

// ── Bookings ───────────────────────────────────────────────────────────────────
export const bookingsApi = {
  getSlots:        (date: string) => apiFetch<TimeSlot[]>(`/bookings/slots?date=${encodeURIComponent(date)}`),
  getBlockedDates: () => apiFetch<string[]>('/bookings/blocked-dates'),
};

// ── Types ──────────────────────────────────────────────────────────────────────
export interface WPUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  billing: {
    address_1: string;
    address_2: string;
    city: string;
    postcode: string;
    phone: string;
  };
}

export interface WCProduct {
  id: number;
  slug: string;
  cleario_type: 'service' | 'item';
  name: string;
  description: string;
  type: 'simple' | 'variable';
  price: string;
  price_html: string;
  variations?: WCVariation[];
}

export interface WCVariation {
  variation_id: number;
  cleario_var_id: string;
  label: string;
  price: number;
  volume_yd3: number;
  weight_kg: number;
}

export interface WCLoadSize {
  id: string;
  label: string;
  price: number;
  volume_yd3: number;
  weight_kg: number;
  variation_id: number;
}

export interface WCOrder {
  id: number;
  status: string;
  date: string;
  total: string;
  service_slug: string;
  booking_date: string;
  time_label: string;
  items: { name: string; quantity: number; total: string }[];
}

export interface TimeSlot {
  id: string;
  label: string;
  time: string;
}

export interface BookingPayload {
  serviceSlug: string;
  serviceTitle: string;
  stepType: string;
  selectedOption: string;
  selectedOptionLabel: string;
  selectedOptionPrice: number;
  cartItems: { id: string; label: string; quantity: number; unitPrice: number }[];
  postcode: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  county: string;
  houseNumber: string;
  date: string;
  timeSlot: string;
  timeSlotLabel: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes: string;
  wasteDescription: string;
  payment_method?: 'cash' | 'stripe';
}

// ── CMS shape ──────────────────────────────────────────────────────────────────
export type CMSPage = Record<string, unknown>;
export type CMSData = Record<string, CMSPage>;